"use client";

import React, { useState, useRef, useCallback, useEffect } from "react";
import Link from "next/link";
import AppLogo from "@/components/ui/AppLogo";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";
import { createClient } from "@/lib/supabase/client";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";

type AdminTab = "hero" | "gallery" | "links" | "videos" | "students" | "phones" | "announce" | "history" | "about";
type SliderLayout = 1 | 2 | 3 | 4;

interface HeroImage { id: string; src: string; slot: number; order: number; }
interface GalleryTopic { id: string; label: string; images: { id: string; src: string; alt: string }[]; }
interface VideoEntry { id: string; title: string; youtubeUrl: string; }
interface StudentRow { student_name: string; class: string; section: string; parent_phone: string; valid: boolean; error?: string; }
interface AnnouncementHistory { id: string; date: string; title: string; targetType: string; numberCount: number; status: "success" | "pending" | "failed"; deliveryMethod?: string; }
interface GallerySource {
  section_name: string;
  drive_folder_link: string;
  max_images: number;
  auto_slide_seconds: number;
  updated_at: string;
}
interface DriveImage { id: string; name: string; thumbUrl: string; fullUrl: string; }

const CLASS_LIST = ["Nursery", "LKG", "UKG", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];
const SECTION_LIST = ["A", "B", "C", "D", "E"];

const SECTION_ORDER = [
  "hero_slider", "correspondent", "principal", "campus", "teachers",
  "admins", "supporting_staff", "sports_events", "cultural_events",
  "tours", "toppers", "hall_of_fame",
];

const SECTION_DISPLAY_NAMES: Record<string, string> = {
  hero_slider: "Hero Slider", correspondent: "Our Correspondent", principal: "Our Principal",
  campus: "Our Campus", teachers: "Our Teachers", admins: "Our Admin Staff",
  supporting_staff: "Supporting Staff", sports_events: "Sports Events",
  cultural_events: "Cultural Events", tours: "Tours", toppers: "Toppers",
  hall_of_fame: "Hall of Fame",
};

// ─── TOAST ───────────────────────────────────────────────────────────────────
const Toast: React.FC<{ message: string; visible: boolean }> = ({ message, visible }) => (
  <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-2 px-5 py-3 bg-green-600 text-white rounded-xl shadow-lg font-heading font-600 text-sm transition-all duration-300 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}>
    <Icon name="CheckCircleIcon" size={16} className="text-white" />
    {message}
  </div>
);

// ─── LOGIN ────────────────────────────────────────────────────────────────────
const LoginScreen: React.FC<{ onLogin: () => void }> = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    await new Promise((r) => setTimeout(r, 900));
    if (username !== "admin" || password !== "Royal@2007") {
      setError("Invalid credentials. Please try again.");
      setLoading(false);
      return;
    }
    setLoading(false);
    onLogin();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary to-primary-dark flex items-center justify-center p-4">
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-accent blur-3xl" />
        <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full bg-white blur-3xl" />
      </div>
      <div className="relative w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="bg-primary p-6 text-center">
            <AppLogo size={56} className="mx-auto mb-3" />
            <h1 className="font-heading font-800 text-white text-xl">Admin Panel</h1>
            <p className="font-body text-white/70 text-sm mb-6">ROYAL SCHOOLS</p>
          </div>
          <div className="p-8">
            <h2 className="font-heading font-700 text-gray-800 text-lg mb-1">Welcome back</h2>
            <p className="font-body text-gray-500 text-sm mb-6">Sign in to manage your school media</p>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-600 text-sm">
                <Icon name="ExclamationCircleIcon" size={16} />
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block font-heading font-600 text-xs text-gray-500 uppercase tracking-wide mb-1.5">Username</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-4 py-3 border border-red-100 rounded-xl font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white text-gray-800"
                  placeholder="Enter username"
                  required
                />
              </div>
              <div>
                <label className="block font-heading font-600 text-xs text-gray-500 uppercase tracking-wide mb-1.5">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-red-100 rounded-xl font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white text-gray-800"
                  placeholder="Enter password"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 bg-primary text-white rounded-xl font-heading font-700 text-sm hover:bg-primary-dark transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <Icon name="LockOpenIcon" size={16} />
                    Sign In
                  </>
                )}
              </button>
            </form>
            <p className="mt-4 text-center text-xs text-gray-500">Secured by Supabase Authentication</p>
          </div>
        </div>
        <div className="text-center mt-6">
          <Link href="/homepage" className="text-white/70 text-sm hover:text-white transition-colors flex items-center justify-center gap-1">
            <Icon name="ArrowLeftIcon" size={14} />Back to Website
          </Link>
        </div>
      </div>
    </div>
  );
};

// ─── HERO SLIDER MANAGER ──────────────────────────────────────────────────────
const HeroSliderManager: React.FC = () => {
  const [layout, setLayout] = useState<SliderLayout>(2);
  const [images, setImages] = useState<HeroImage[]>([]);
  const [uploading, setUploading] = useState(false);
  const [loadingImages, setLoadingImages] = useState(true);
  const [toast, setToast] = useState({ visible: false, message: "" });
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const showToast = (message: string) => {
    setToast({ visible: true, message });
    setTimeout(() => setToast({ visible: false, message: "" }), 2500);
  };

  useEffect(() => {
    const loadImages = async () => {
      const supabase = createClient();
      const { data, error } = await supabase.from("hero_images").select("*").order("slot", { ascending: true }).order("sort_order", { ascending: true });
      if (error) { console.error("Failed to load hero images:", error.message); }
      else if (data) {
        setImages(data.map((row: { id: string; src: string; slot: number; sort_order: number }) => ({ id: row.id, src: row.src, slot: row.slot, order: row.sort_order })));
      }
      setLoadingImages(false);
    };
    loadImages();
  }, []);

  const handleUpload = (slot: number) => {
    if (images.filter((i) => i.slot === slot).length >= 25) { alert("Maximum 25 images per slot"); return; }
    fileInputRefs.current[slot]?.click();
  };

  const handleFileChange = async (slot: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const remaining = 25 - images.filter((i) => i.slot === slot).length;
    const filesToUpload = Array.from(files).slice(0, remaining);
    setUploading(true);
    const supabase = createClient();
    for (const file of filesToUpload) {
      const ext = file.name.split(".").pop();
      const fileName = `hero/slot${slot}_${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
      const { data: storageData, error: storageError } = await supabase.storage.from("images").upload(fileName, file, { upsert: false });
      if (storageError) { console.error("Upload error:", storageError.message); continue; }
      const { data: urlData } = supabase.storage.from("images").getPublicUrl(storageData.path);
      const sortOrder = images.filter((i) => i.slot === slot).length;
      const { data: dbRow, error: dbError } = await supabase.from("hero_images").insert({ src: urlData.publicUrl, slot, sort_order: sortOrder }).select().single();
      if (dbError) {
        setImages((prev) => [...prev, { id: `h${Date.now()}`, src: urlData.publicUrl, slot, order: sortOrder }]);
      } else {
        setImages((prev) => [...prev, { id: dbRow.id, src: dbRow.src, slot, order: dbRow.sort_order }]);
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const deleteImage = async (id: string) => {
    const supabase = createClient();
    await supabase.from("hero_images").delete().eq("id", id);
    setImages((prev) => prev.filter((i) => i.id !== id));
  };

  const handleDragEnd = async (result: DropResult, slot: number) => {
    if (!result.destination || result.source.index === result.destination.index) return;
    const slotImages = images.filter((i) => i.slot === slot);
    const reordered = Array.from(slotImages);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    const updatedReordered = reordered.map((img, idx) => ({ ...img, order: idx }));
    setImages((prev) => [...prev.filter((i) => i.slot !== slot), ...updatedReordered]);
    const supabase = createClient();
    await Promise.all(updatedReordered.map((img) => supabase.from("hero_images").update({ sort_order: img.order }).eq("id", img.id)));
    showToast("Order saved");
  };

  return (
    <>
      <Toast message={toast.message} visible={toast.visible} />
      <div className="space-y-6">
        <div className="bg-white rounded-2xl border border-red-100 p-6">
          <h3 className="font-heading font-700 text-gray-800 text-base mb-4 flex items-center gap-2">
            <Icon name="ViewColumnsIcon" size={18} className="text-primary" />Layout Mode
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {([1, 2, 3, 4] as SliderLayout[]).map((n) => (
              <button key={n} onClick={() => setLayout(n)} className={`p-4 rounded-xl border-2 transition-all duration-200 ${layout === n ? "border-primary bg-primary text-white" : "border-red-100 bg-white text-gray-700 hover:border-primary/50"}`}>
                <div className="grid gap-1 mb-2" style={{ gridTemplateColumns: `repeat(${n === 4 ? 2 : n}, 1fr)` }}>
                  {Array.from({ length: n }).map((_, i) => <div key={i} className={`h-4 rounded ${layout === n ? "bg-white/50" : "bg-primary/20"}`} />)}
                </div>
                <span className="font-heading font-700 text-sm">{n} Part{n > 1 ? "s" : ""}</span>
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-3 font-body">Current layout: <strong className="text-primary">{layout}-Part</strong> · Images rotate every 3 seconds</p>
        </div>
        {loadingImages ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            <span className="ml-3 text-sm text-gray-500 font-body">Loading images...</span>
          </div>
        ) : (
          Array.from({ length: layout }).map((_, slot) => {
            const slotImages = images.filter((i) => i.slot === slot).sort((a, b) => a.order - b.order);
            return (
              <div key={slot} className="bg-white rounded-2xl border border-red-100 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-700 text-gray-800 text-base">
                    Slot {slot + 1}<span className="ml-2 text-xs text-gray-500 font-body">({slotImages.length}/25 images)</span>
                  </h3>
                  <button onClick={() => handleUpload(slot)} disabled={uploading}
                    className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-lg font-heading font-600 text-sm hover:bg-primary-dark transition-colors disabled:opacity-60">
                    <Icon name="CloudArrowUpIcon" size={15} />{uploading ? "Uploading..." : "Upload Image"}
                  </button>
                  <input ref={(el) => { fileInputRefs.current[slot] = el; }} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFileChange(slot, e)} />
                </div>
                {slotImages.length === 0 ? (
                  <div className="border-2 border-dashed border-red-100 rounded-xl p-8 text-center">
                    <Icon name="PhotoIcon" size={32} className="text-gray-400 mx-auto mb-2 opacity-40" />
                    <p className="font-body text-sm text-gray-500">No images yet. Click Upload to add images.</p>
                  </div>
                ) : (
                  <DragDropContext onDragEnd={(result) => handleDragEnd(result, slot)}>
                    <Droppable droppableId={`hero-slot-${slot}`} direction="horizontal">
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-3">
                          {slotImages.map((img, index) => (
                            <Draggable key={img.id} draggableId={img.id} index={index}>
                              {(dragProvided, dragSnapshot) => (
                                <div ref={dragProvided.innerRef} {...dragProvided.draggableProps}
                                  className={`relative group aspect-square rounded-xl overflow-hidden border transition-shadow ${dragSnapshot.isDragging ? "border-primary shadow-lg ring-2 ring-primary/40 z-50" : "border-red-100"}`}>
                                  <AppImage src={img.src} alt="" fill className="object-cover" unoptimized />
                                  <div {...dragProvided.dragHandleProps} className="absolute top-1 left-1 w-6 h-6 bg-black/60 rounded-md flex items-center justify-center cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                    <Icon name="Bars3Icon" size={12} className="text-white" />
                                  </div>
                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                                    <div className="opacity-0 group-hover:opacity-100 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center transition-opacity">
                                      <Icon name="TrashIcon" size={14} className="text-white" />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </div>
            );
          })
        )}
      </div>
    </>
  );
};

// ─── DRIVE IMAGE PICKER MODAL ─────────────────────────────────────────────────
interface DrivePickerProps {
  topicId: string;
  topicLabel: string;
  onClose: () => void;
  onImported: (topicId: string, images: { id: string; src: string; alt: string }[]) => void;
}

const DriveImagePicker: React.FC<DrivePickerProps> = ({ topicId, topicLabel, onClose, onImported }) => {
  const [folderLink, setFolderLink] = useState("");
  const [driveImages, setDriveImages] = useState<DriveImage[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [importing, setImporting] = useState(false);

  const fetchImages = async () => {
    if (!folderLink.trim()) { setError("Please enter a Google Drive folder link or ID."); return; }
    setLoading(true);
    setError("");
    setDriveImages([]);
    setSelected(new Set());
    try {
      const res = await fetch(`/api/drive/images?folderId=${encodeURIComponent(folderLink.trim())}&pageSize=100`);
      const data = await res.json();
      if (data.error) { setError(data.error); }
      else if (!data.images || data.images.length === 0) { setError("No images found in this folder. Make sure the folder is shared publicly (Anyone with the link can view)."); }
      else { setDriveImages(data.images); }
    } catch {
      setError("Failed to connect to Google Drive. Please check the folder link.");
    }
    setLoading(false);
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(driveImages.map((i) => i.id)));
  const clearAll = () => setSelected(new Set());

  const handleImport = async () => {
    if (selected.size === 0) { setError("Please select at least one image."); return; }
    setImporting(true);
    const supabase = createClient();
    const toImport = driveImages.filter((img) => selected.has(img.id));
    const { data: existing } = await supabase.from("gallery_images").select("sort_order").eq("topic_id", topicId).order("sort_order", { ascending: false }).limit(1);
    let nextOrder = existing && existing.length > 0 ? (existing[0].sort_order + 1) : 0;

    const inserted: { id: string; src: string; alt: string }[] = [];
    for (const img of toImport) {
      const { data: row, error: dbErr } = await supabase.from("gallery_images")
        .insert({ topic_id: topicId, src: img.fullUrl, alt: img.name, sort_order: nextOrder++ })
        .select().single();
      if (!dbErr && row) inserted.push({ id: row.id, src: row.src, alt: row.alt });
    }
    setImporting(false);
    onImported(topicId, inserted);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-red-100">
          <div>
            <h3 className="font-heading font-700 text-gray-800 text-base flex items-center gap-2">
              <Icon name="FolderOpenIcon" size={18} className="text-primary" />
              Import from Google Drive
            </h3>
            <p className="text-xs text-gray-500 font-body mt-0.5">Topic: <strong>{topicLabel}</strong></p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center hover:bg-red-100 transition-colors">
            <Icon name="XMarkIcon" size={16} className="text-red-500" />
          </button>
        </div>

        {/* Folder input */}
        <div className="p-5 border-b border-red-50">
          <label className="block font-heading font-600 text-xs text-gray-500 uppercase tracking-wide mb-2">Google Drive Folder Link or ID</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={folderLink}
              onChange={(e) => setFolderLink(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchImages()}
              placeholder="https://drive.google.com/drive/folders/... or folder ID"
              className="flex-1 px-4 py-2.5 border border-red-100 rounded-xl font-body text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
            <button onClick={fetchImages} disabled={loading}
              className="px-5 py-2.5 bg-primary text-white rounded-xl font-heading font-600 text-sm hover:bg-primary-dark transition-colors disabled:opacity-60 flex items-center gap-2 whitespace-nowrap">
              {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="MagnifyingGlassIcon" size={15} />}
              {loading ? "Loading..." : "Load Images"}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2 font-body">
            ⚠️ The folder must be shared as <strong>"Anyone with the link can view"</strong> in Google Drive.
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-5 mt-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2 text-red-600 text-sm font-body">
            <Icon name="ExclamationCircleIcon" size={16} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Image grid */}
        {driveImages.length > 0 && (
          <>
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <span className="text-sm font-heading font-600 text-gray-800">{driveImages.length} images found · {selected.size} selected</span>
              <div className="flex gap-2">
                <button onClick={selectAll} className="text-xs px-3 py-1.5 bg-primary/10 text-primary rounded-lg font-heading font-600 hover:bg-primary/20 transition-colors">Select All</button>
                <button onClick={clearAll} className="text-xs px-3 py-1.5 bg-red-50 text-red-500 rounded-lg font-heading font-600 hover:bg-red-100 transition-colors">Clear</button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-5 pb-4">
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                {driveImages.map((img) => {
                  const isSelected = selected.has(img.id);
                  return (
                    <button key={img.id} onClick={() => toggleSelect(img.id)}
                      className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all duration-150 ${isSelected ? "border-primary ring-2 ring-primary/30" : "border-transparent hover:border-primary/40"}`}>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={img.thumbUrl} alt={img.name} className="w-full h-full object-cover" loading="lazy"
                        onError={(e) => { (e.target as HTMLImageElement).src = "/assets/images/no_image.png"; }} />
                      {isSelected && (
                        <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                          <div className="w-7 h-7 bg-primary rounded-full flex items-center justify-center shadow-md">
                            <Icon name="CheckIcon" size={14} className="text-white" />
                          </div>
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-1.5 py-1">
                        <p className="text-white text-[10px] truncate font-body">{img.name}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Footer */}
        <div className="p-5 border-t border-red-100 flex items-center justify-between">
          <button onClick={onClose} className="px-5 py-2.5 border border-red-100 text-gray-500 rounded-xl font-heading font-600 text-sm hover:bg-red-50 transition-colors">Cancel</button>
          {driveImages.length > 0 && (
            <button onClick={handleImport} disabled={importing || selected.size === 0}
              className="px-6 py-2.5 bg-primary text-white rounded-xl font-heading font-700 text-sm hover:bg-primary-dark transition-colors disabled:opacity-60 flex items-center gap-2">
              {importing ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="CloudArrowDownIcon" size={15} />}
              {importing ? "Importing..." : `Import ${selected.size} Image${selected.size !== 1 ? "s" : ""}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── GALLERY MANAGER ──────────────────────────────────────────────────────────
const GalleryManager: React.FC = () => {
  const [topics, setTopics] = useState<GalleryTopic[]>([]);
  const [activeTopic, setActiveTopic] = useState<string | null>(null);
  const [newTopicLabel, setNewTopicLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: "" });
  const [drivePickerTopic, setDrivePickerTopic] = useState<{ id: string; label: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showToast = (message: string) => {
    setToast({ visible: true, message });
    setTimeout(() => setToast({ visible: false, message: "" }), 2500);
  };

  useEffect(() => {
    const loadTopics = async () => {
      const supabase = createClient();
      const { data: topicRows } = await supabase.from("gallery_topics").select("*").order("sort_order", { ascending: true });
      if (!topicRows) { setLoading(false); return; }
      const { data: imageRows } = await supabase.from("gallery_images").select("*").order("sort_order", { ascending: true });
      const topicsWithImages: GalleryTopic[] = topicRows.map((t: { id: string; label: string }) => ({
        id: t.id,
        label: t.label,
        images: (imageRows ?? []).filter((img: { topic_id: string }) => img.topic_id === t.id).map((img: { id: string; src: string; alt: string }) => ({ id: img.id, src: img.src, alt: img.alt })),
      }));
      setTopics(topicsWithImages);
      if (topicsWithImages.length > 0) setActiveTopic(topicsWithImages[0].id);
      setLoading(false);
    };
    loadTopics();
  }, []);

  const addTopic = async () => {
    const label = newTopicLabel.trim();
    if (!label) return;
    const id = label.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    const supabase = createClient();
    const { error } = await supabase.from("gallery_topics").insert({ id, label, sort_order: topics.length });
    if (error) { alert("Failed to add topic: " + error.message); return; }
    const newTopic: GalleryTopic = { id, label, images: [] };
    setTopics((prev) => [...prev, newTopic]);
    setActiveTopic(id);
    setNewTopicLabel("");
    showToast(`Topic "${label}" added`);
  };

  const deleteTopic = async (topicId: string) => {
    if (!confirm("Delete this topic and all its images?")) return;
    const supabase = createClient();
    await supabase.from("gallery_images").delete().eq("topic_id", topicId);
    await supabase.from("gallery_topics").delete().eq("id", topicId);
    const remaining = topics.filter((t) => t.id !== topicId);
    setTopics(remaining);
    setActiveTopic(remaining.length > 0 ? remaining[0].id : null);
    showToast("Topic deleted");
  };

  const handleUpload = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!activeTopic) return;
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    const supabase = createClient();
    const currentTopic = topics.find((t) => t.id === activeTopic);
    let nextOrder = currentTopic?.images.length ?? 0;

    for (const file of Array.from(files)) {
      const ext = file.name.split(".").pop();
      const fileName = `gallery/${activeTopic}_${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
      const { data: storageData, error: storageError } = await supabase.storage.from("images").upload(fileName, file, { upsert: false });
      if (storageError) { console.error("Upload error:", storageError.message); continue; }
      const { data: urlData } = supabase.storage.from("images").getPublicUrl(storageData.path);
      const { data: row, error: dbErr } = await supabase.from("gallery_images")
        .insert({ topic_id: activeTopic, src: urlData.publicUrl, alt: file.name, sort_order: nextOrder++ })
        .select().single();
      if (!dbErr && row) {
        setTopics((prev) => prev.map((t) => t.id === activeTopic ? { ...t, images: [...t.images, { id: row.id, src: row.src, alt: row.alt }] } : t));
      }
    }
    setUploading(false);
    e.target.value = "";
    showToast("Images uploaded");
  };

  const deleteImage = async (topicId: string, imageId: string) => {
    const supabase = createClient();
    await supabase.from("gallery_images").delete().eq("id", imageId);
    setTopics((prev) => prev.map((t) => t.id === topicId ? { ...t, images: t.images.filter((img) => img.id !== imageId) } : t));
  };

  const handleDriveImported = (topicId: string, newImages: { id: string; src: string; alt: string }[]) => {
    setTopics((prev) => prev.map((t) => t.id === topicId ? { ...t, images: [...t.images, ...newImages] } : t));
    showToast(`${newImages.length} image${newImages.length !== 1 ? "s" : ""} imported from Drive`);
  };

  const currentTopic = topics.find((t) => t.id === activeTopic);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        <span className="ml-3 text-sm text-gray-500 font-body">Loading gallery...</span>
      </div>
    );
  }

  return (
    <>
      <Toast message={toast.message} visible={toast.visible} />
      {drivePickerTopic && (
        <DriveImagePicker
          topicId={drivePickerTopic.id}
          topicLabel={drivePickerTopic.label}
          onClose={() => setDrivePickerTopic(null)}
          onImported={handleDriveImported}
        />
      )}

      <div className="flex gap-4 h-[calc(100vh-220px)] min-h-[500px]">
        {/* Left panel — Topics */}
        <div className="w-64 flex-shrink-0 bg-white rounded-2xl border border-red-100 flex flex-col overflow-hidden">
          <div className="p-4 border-b border-red-50">
            <h3 className="font-heading font-700 text-gray-800 text-sm flex items-center gap-2">
              <Icon name="FolderIcon" size={16} className="text-primary" />Gallery Topics
            </h3>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {topics.length === 0 ? (
              <p className="text-xs text-gray-500 font-body text-center py-6">No topics yet</p>
            ) : (
              topics.map((topic) => (
                <button key={topic.id} onClick={() => setActiveTopic(topic.id)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-left transition-all duration-150 group ${activeTopic === topic.id ? "bg-primary text-white" : "hover:bg-red-50 text-gray-800"}`}>
                  <span className="font-heading font-600 text-sm truncate">{topic.label}</span>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className={`text-xs font-body ${activeTopic === topic.id ? "text-white/70" : "text-gray-500"}`}>{topic.images.length}</span>
                    <div role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); deleteTopic(topic.id); }} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); deleteTopic(topic.id); } }}
                      className={`w-5 h-5 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer ${activeTopic === topic.id ? "bg-white/20 hover:bg-white/30" : "bg-red-100 hover:bg-red-200"}`}>
                      <Icon name="TrashIcon" size={10} className={activeTopic === topic.id ? "text-white" : "text-red-500"} />
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
          {/* Add topic */}
          <div className="p-3 border-t border-red-50">
            <input
              type="text"
              value={newTopicLabel}
              onChange={(e) => setNewTopicLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addTopic()}
              placeholder="New topic name..."
              className="w-full px-3 py-2 border border-red-100 rounded-lg font-body text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary mb-2"
            />
            <button onClick={addTopic} disabled={!newTopicLabel.trim()}
              className="w-full py-2 bg-primary text-white rounded-lg font-heading font-600 text-xs hover:bg-primary-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5">
              <Icon name="PlusIcon" size={13} />Add Topic
            </button>
          </div>
        </div>

        {/* Right panel — Images */}
        <div className="flex-1 bg-white rounded-2xl border border-red-100 flex flex-col overflow-hidden">
          {!currentTopic ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Icon name="FolderOpenIcon" size={40} className="text-gray-400 mx-auto mb-3 opacity-30" />
                <p className="font-body text-sm text-gray-500">Select a topic to manage images</p>
              </div>
            </div>
          ) : (
            <>
              {/* Right header */}
              <div className="flex items-center justify-between p-4 border-b border-red-50">
                <div>
                  <h3 className="font-heading font-700 text-gray-800 text-base">{currentTopic.label}</h3>
                  <p className="text-xs text-gray-500 font-body">{currentTopic.images.length} image{currentTopic.images.length !== 1 ? "s" : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setDrivePickerTopic({ id: currentTopic.id, label: currentTopic.label })}
                    className="flex items-center gap-1.5 px-4 py-2 bg-blue-50 text-blue-600 border border-blue-200 rounded-lg font-heading font-600 text-sm hover:bg-blue-100 transition-colors">
                    <Icon name="FolderOpenIcon" size={15} />Drive
                  </button>
                  <button onClick={handleUpload} disabled={uploading}
                    className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-lg font-heading font-600 text-sm hover:bg-primary-dark transition-colors disabled:opacity-60">
                    <Icon name="CloudArrowUpIcon" size={15} />{uploading ? "Uploading..." : "Upload"}
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileChange} />
                </div>
              </div>

              {/* Images grid */}
              <div className="flex-1 overflow-y-auto p-4">
                {currentTopic.images.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center border-2 border-dashed border-red-100 rounded-xl">
                    <Icon name="PhotoIcon" size={40} className="text-gray-400 mb-3 opacity-30" />
                    <p className="font-body text-sm text-gray-500 mb-1">No images in this topic</p>
                    <p className="font-body text-xs text-gray-500">Upload images or import from Google Drive</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                    {currentTopic.images.map((img) => (
                      <div key={img.id} className="relative group aspect-square rounded-xl overflow-hidden border border-red-100">
                        <AppImage src={img.src} alt={img.alt} fill className="object-cover" unoptimized />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                          <button onClick={() => deleteImage(currentTopic.id, img.id)}
                            className="opacity-0 group-hover:opacity-100 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center transition-opacity" aria-label="Delete image">
                            <Icon name="TrashIcon" size={14} className="text-white" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

// ─── GALLERY LINKS MANAGER ────────────────────────────────────────────────────
const GalleryLinksManager: React.FC = () => {
  const [sources, setSources] = useState<GallerySource[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [toast, setToast] = useState({ visible: false, message: "" });

  const showToast = (message: string) => {
    setToast({ visible: true, message });
    setTimeout(() => setToast({ visible: false, message: "" }), 2500);
  };

  useEffect(() => {
    const loadSources = async () => {
      const supabase = createClient();
      const { data, error } = await supabase.from("gallery_sources").select("*").order("section_name");
      if (error) console.error("Failed to load gallery sources:", error.message);
      else setSources(data ?? []);
      setLoading(false);
    };
    loadSources();
  }, []);

  const updateSource = (sectionName: string, field: keyof GallerySource, value: string | number) => {
    setSources((prev) => prev.map((s) => s.section_name === sectionName ? { ...s, [field]: value } : s));
  };

  const saveSource = async (source: GallerySource) => {
    setSaving(source.section_name);
    const supabase = createClient();
    const { error } = await supabase
      .from("gallery_sources")
      .upsert(
        {
          section_name: source.section_name,
          drive_folder_link: source.drive_folder_link,
          max_images: source.max_images,
          auto_slide_seconds: source.auto_slide_seconds,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "section_name" }
      );
    if (error) alert("Failed to save: " + error.message);
    else showToast(`Saved ${SECTION_DISPLAY_NAMES[source.section_name] ?? source.section_name}`);
    setSaving(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        <span className="ml-3 text-sm text-gray-500 font-body">Loading...</span>
      </div>
    );
  }

  const orderedSources = SECTION_ORDER.map((name) => sources.find((s) => s.section_name === name)).filter(Boolean) as GallerySource[];
  const extraSources = sources.filter((s) => !SECTION_ORDER.includes(s.section_name));

  return (
    <>
      <Toast message={toast.message} visible={toast.visible} />
      <div className="space-y-3">
        {[...orderedSources, ...extraSources].map((source) => (
          <div key={source.section_name} className="bg-white rounded-2xl border border-red-100 p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h4 className="font-heading font-700 text-gray-800 text-sm mb-3">
                  {SECTION_DISPLAY_NAMES[source.section_name] ?? source.section_name}
                </h4>
                <div className="space-y-2">
                  <div>
                    <label className="block font-heading font-600 text-xs text-gray-500 uppercase tracking-wide mb-1">Drive Folder Link</label>
                    <input
                      type="text"
                      value={source.drive_folder_link ?? ""}
                      onChange={(e) => updateSource(source.section_name, "drive_folder_link", e.target.value)}
                      placeholder="https://drive.google.com/drive/folders/..."
                      className="w-full px-3 py-2 border border-red-100 rounded-lg font-body text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    />
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="block font-heading font-600 text-xs text-gray-500 uppercase tracking-wide mb-1">Max Images</label>
                      <input
                        type="number"
                        min={1} max={100}
                        value={source.max_images ?? 25}
                        onChange={(e) => updateSource(source.section_name, "max_images", parseInt(e.target.value) || 25)}
                        className="w-full px-3 py-2 border border-red-100 rounded-lg font-body text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block font-heading font-600 text-xs text-gray-500 uppercase tracking-wide mb-1">Slide Interval (sec)</label>
                      <input
                        type="number"
                        min={1} max={30}
                        value={source.auto_slide_seconds ?? 3}
                        onChange={(e) => updateSource(source.section_name, "auto_slide_seconds", parseInt(e.target.value) || 3)}
                        className="w-full px-3 py-2 border border-red-100 rounded-lg font-body text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <button onClick={() => saveSource(source)} disabled={saving === source.section_name}
                className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-lg font-heading font-600 text-sm hover:bg-primary-dark transition-colors disabled:opacity-60 mt-6">
                {saving === source.section_name ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="CheckIcon" size={14} />}
                Save
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

// ─── VIDEOS MANAGER ───────────────────────────────────────────────────────────
const VideosManager: React.FC = () => {
  const [videos, setVideos] = useState<VideoEntry[]>([]);
  const [newTitle, setNewTitle] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: "" });

  const showToast = (message: string) => {
    setToast({ visible: true, message });
    setTimeout(() => setToast({ visible: false, message: "" }), 2500);
  };

  useEffect(() => {
    const load = async () => {
      const supabase = createClient();
      const { data } = await supabase.from("school_videos").select("*").order("created_at", { ascending: false });
      setVideos((data ?? []).map((v: { id: string; title: string; youtube_url: string }) => ({ id: v.id, title: v.title, youtubeUrl: v.youtube_url })));
      setLoading(false);
    };
    load();
  }, []);

  const addVideo = async () => {
    if (!newTitle.trim() || !newUrl.trim()) return;
    setSaving(true);
    const supabase = createClient();
    const { data, error } = await supabase.from("school_videos").insert({ title: newTitle.trim(), youtube_url: newUrl.trim() }).select().single();
    if (error) { alert("Failed to add video: " + error.message); }
    else if (data) {
      setVideos((prev) => [{ id: data.id, title: data.title, youtubeUrl: data.youtube_url }, ...prev]);
      setNewTitle(""); setNewUrl("");
      showToast("Video added");
    }
    setSaving(false);
  };

  const deleteVideo = async (id: string) => {
    const supabase = createClient();
    await supabase.from("school_videos").delete().eq("id", id);
    setVideos((prev) => prev.filter((v) => v.id !== id));
    showToast("Video deleted");
  };

  return (
    <>
      <Toast message={toast.message} visible={toast.visible} />
      <div className="space-y-4">
        <div className="bg-white rounded-2xl border border-red-100 p-5">
          <h3 className="font-heading font-700 text-gray-800 text-sm mb-4 flex items-center gap-2">
            <Icon name="PlusCircleIcon" size={16} className="text-primary" />Add New Video
          </h3>
          <div className="space-y-3">
            <input type="text" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Video title"
              className="w-full px-4 py-2.5 border border-red-100 rounded-xl font-body text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
            <input type="text" value={newUrl} onChange={(e) => setNewUrl(e.target.value)} placeholder="YouTube URL (https://youtube.com/watch?v=...)"
              className="w-full px-4 py-2.5 border border-red-100 rounded-xl font-body text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
            <button onClick={addVideo} disabled={saving || !newTitle.trim() || !newUrl.trim()}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl font-heading font-600 text-sm hover:bg-primary-dark transition-colors disabled:opacity-60">
              <Icon name="PlusIcon" size={15} />{saving ? "Adding..." : "Add Video"}
            </button>
          </div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        ) : videos.length === 0 ? (
          <div className="bg-white rounded-2xl border border-red-100 p-10 text-center">
            <Icon name="VideoCameraIcon" size={32} className="text-gray-400 mx-auto mb-2 opacity-30" />
            <p className="font-body text-sm text-gray-500">No videos added yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {videos.map((v) => (
              <div key={v.id} className="bg-white rounded-2xl border border-red-100 p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon name="PlayIcon" size={18} className="text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-heading font-600 text-sm text-gray-800 truncate">{v.title}</p>
                    <p className="font-body text-xs text-gray-500 break-all">{v.youtubeUrl}</p>
                  </div>
                </div>
                <button onClick={() => deleteVideo(v.id)} className="flex-shrink-0 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors">
                  <Icon name="TrashIcon" size={14} className="text-white" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

// ─── ABOUT US IMAGE MANAGER ───────────────────────────────────────────────────
const AboutUsImageManager: React.FC = () => {
  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ visible: false, message: "" });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showToast = (message: string) => {
    setToast({ visible: true, message });
    setTimeout(() => setToast({ visible: false, message: "" }), 2500);
  };

  useEffect(() => {
    const loadImage = async () => {
      const supabase = createClient();
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("id", "about_us_image")
        .single();
      if (data?.value) setCurrentImageUrl(data.value);
      setLoading(false);
    };
    loadImage();
  }, []);

  const handleUpload = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const supabase = createClient();
    const ext = file.name.split(".").pop();
    const fileName = `about/about_us_${Date.now()}.${ext}`;
    const { data: storageData, error: storageError } = await supabase.storage
      .from("images")
      .upload(fileName, file, { upsert: false });
    if (storageError) {
      alert("Upload failed: " + storageError.message);
      setUploading(false);
      e.target.value = "";
      return;
    }
    const { data: urlData } = supabase.storage.from("images").getPublicUrl(storageData.path);
    const publicUrl = urlData.publicUrl;
    const { error: upsertError } = await supabase
      .from("site_settings")
      .upsert({ id: "about_us_image", value: publicUrl }, { onConflict: "id" });
    if (upsertError) {
      alert("Failed to save image URL: " + upsertError.message);
    } else {
      setCurrentImageUrl(publicUrl);
      showToast("About Us image updated successfully!");
    }
    setUploading(false);
    e.target.value = "";
  };

  const handleRemove = async () => {
    if (!confirm("Remove the current About Us image?")) return;
    const supabase = createClient();
    await supabase.from("site_settings").delete().eq("id", "about_us_image");
    setCurrentImageUrl(null);
    showToast("Image removed");
  };

  return (
    <>
      <Toast message={toast.message} visible={toast.visible} />
      <div className="space-y-6 max-w-2xl">
        <div className="bg-white rounded-2xl border border-red-100 p-6">
          <h3 className="font-heading font-700 text-gray-800 text-base mb-1 flex items-center gap-2">
            <Icon name="InformationCircleIcon" size={18} className="text-primary" />
            About Us Page Image
          </h3>
          <p className="font-body text-sm text-gray-500 mb-6">
            Upload the main image displayed on the About Us page. Uploading a new image will replace the current one.
          </p>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              <span className="ml-3 text-sm text-gray-500 font-body">Loading...</span>
            </div>
          ) : currentImageUrl ? (
            <div className="space-y-4">
              <div className="relative rounded-2xl overflow-hidden border border-red-100 bg-gray-50" style={{ aspectRatio: "16/7" }}>
                <AppImage
                  src={currentImageUrl}
                  alt="Current About Us page image"
                  fill
                  className="object-cover"
                  unoptimized
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl font-heading font-600 text-sm hover:bg-primary-dark transition-colors disabled:opacity-60"
                >
                  <Icon name="ArrowPathIcon" size={15} />
                  {uploading ? "Uploading..." : "Replace Image"}
                </button>
                <button
                  onClick={handleRemove}
                  className="flex items-center gap-2 px-5 py-2.5 border border-red-200 text-red-500 rounded-xl font-heading font-600 text-sm hover:bg-red-50 transition-colors"
                >
                  <Icon name="TrashIcon" size={15} />
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <div
              onClick={handleUpload}
              className="border-2 border-dashed border-red-100 rounded-2xl p-12 text-center cursor-pointer hover:border-primary/40 hover:bg-red-50/30 transition-all"
            >
              <Icon name="CloudArrowUpIcon" size={40} className="text-gray-400 mx-auto mb-3 opacity-40" />
              <p className="font-heading font-700 text-gray-700 text-sm mb-1">Click to upload About Us image</p>
              <p className="font-body text-xs text-gray-500">JPG, PNG, WebP supported · Max 10MB</p>
              {uploading && (
                <div className="mt-4 flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                  <span className="text-sm text-primary font-body">Uploading...</span>
                </div>
              )}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      </div>
    </>
  );
};

// ─── STUDENT MANAGER ──────────────────────────────────────────────────────────
interface StudentRecord { id: string; student_name: string; class: string; section: string; parent_phone: string; }

const StudentManager: React.FC = () => {
  const [students, setStudents] = useState<StudentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filterClass, setFilterClass] = useState("all");
  const [filterSection, setFilterSection] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [csvRows, setCsvRows] = useState<StudentRow[]>([]);
  const [csvStep, setCsvStep] = useState<"idle" | "preview" | "importing">("idle");
  const [toast, setToast] = useState({ visible: false, message: "" });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showToast = (message: string) => {
    setToast({ visible: true, message });
    setTimeout(() => setToast({ visible: false, message: "" }), 2500);
  };

  const loadStudents = useCallback(async () => {
    setLoading(true);
    const supabase = createClient();
    const { data, error } = await supabase.from("students").select("*").order("class").order("section").order("student_name");
    if (error) console.error("Failed to load students:", error.message);
    else setStudents(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { loadStudents(); }, [loadStudents]);

  const parseCsv = (text: string): StudentRow[] => {
    const lines = text.trim().split("\n");
    const rows: StudentRow[] = [];
    const startIdx = lines[0]?.toLowerCase().includes("name") ? 1 : 0;
    for (let i = startIdx; i < lines.length; i++) {
      const cols = lines[i].split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
      if (cols.length < 4) { rows.push({ student_name: lines[i], class: "", section: "", parent_phone: "", valid: false, error: "Not enough columns (need: Name, Class, Section, Phone)" }); continue; }
      const [student_name, cls, section, parent_phone] = cols;
      const valid = !!student_name && !!cls && !!parent_phone && /^[6-9]\d{9}$/.test(parent_phone.replace(/\D/g, "").slice(-10));
      rows.push({ student_name, class: cls, section: section ?? "", parent_phone: parent_phone.replace(/\D/g, "").slice(-10), valid, error: valid ? undefined : "Invalid data" });
    }
    return rows;
  };

  const handleCsvFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const rows = parseCsv(text);
      setCsvRows(rows);
      setCsvStep("preview");
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const importCsv = async () => {
    const validRows = csvRows.filter((r) => r.valid);
    if (validRows.length === 0) return;
    setCsvStep("importing");
    setUploading(true);
    const supabase = createClient();
    const toInsert = validRows.map((r) => ({ student_name: r.student_name, class: r.class, section: r.section, parent_phone: r.parent_phone }));
    const { error } = await supabase.from("students").upsert(toInsert, { onConflict: "student_name,class,section,parent_phone", ignoreDuplicates: true });
    if (error) { alert("Import failed: " + error.message); }
    else { showToast(`${validRows.length} students imported`); await loadStudents(); }
    setCsvStep("idle");
    setCsvRows([]);
    setUploading(false);
  };

  const deleteStudent = async (id: string) => {
    const supabase = createClient();
    await supabase.from("students").delete().eq("id", id);
    setStudents((prev) => prev.filter((s) => s.id !== id));
    showToast("Student removed");
  };

  const clearAll = async () => {
    if (!confirm("Delete ALL students? This cannot be undone.")) return;
    const supabase = createClient();
    await supabase.from("students").delete().neq("id", "00000000-0000-0000-0000-000000000000");
    setStudents([]);
    showToast("All students cleared");
  };

  const filtered = students.filter((s) => {
    if (filterClass !== "all" && s.class !== filterClass) return false;
    if (filterSection !== "all" && s.section !== filterSection) return false;
    if (searchQuery && !s.student_name.toLowerCase().includes(searchQuery.toLowerCase()) && !s.parent_phone.includes(searchQuery)) return false;
    return true;
  });

  return (
    <>
      <Toast message={toast.message} visible={toast.visible} />
      <div className="space-y-5">
        {/* Upload CSV */}
        <div className="bg-white rounded-2xl border border-red-100 p-5">
          <h3 className="font-heading font-700 text-gray-800 text-sm mb-3 flex items-center gap-2">
            <Icon name="TableCellsIcon" size={16} className="text-primary" />Import Students via CSV
          </h3>
          <p className="font-body text-xs text-gray-500 mb-3">CSV format: <strong>Name, Class, Section, Phone</strong> (header row optional)</p>
          <div className="flex items-center gap-3">
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl font-heading font-600 text-sm hover:bg-primary-dark transition-colors disabled:opacity-60">
              <Icon name="DocumentArrowUpIcon" size={15} />Upload CSV
            </button>
            {students.length > 0 && (
              <button onClick={clearAll} className="flex items-center gap-2 px-4 py-2.5 border border-red-200 text-red-500 rounded-lg font-heading font-600 text-sm hover:bg-red-50 transition-colors">
                <Icon name="TrashIcon" size={14} />Clear All
              </button>
            )}
            <span className="font-body text-xs text-gray-500">{students.length} students in database</span>
          </div>
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleCsvFile} />
        </div>

        {/* CSV Preview */}
        {csvStep !== "idle" && (
          <div className="bg-white rounded-2xl border border-red-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-700 text-gray-800 text-sm flex items-center gap-2">
                <Icon name="ClipboardDocumentListIcon" size={16} className="text-primary" />
                CSV Preview — {csvRows.filter((r) => r.valid).length} valid / {csvRows.length} total rows
              </h3>
              <div className="flex gap-2">
                <button onClick={() => { setCsvStep("idle"); setCsvRows([]); }}
                  className="px-4 py-2 border border-red-100 text-gray-500 rounded-lg font-heading font-600 text-xs hover:bg-red-50 transition-colors">Cancel</button>
                <button onClick={importCsv} disabled={csvStep === "importing" || csvRows.filter((r) => r.valid).length === 0}
                  className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-lg font-heading font-600 text-xs hover:bg-primary-dark transition-colors disabled:opacity-60">
                  {csvStep === "importing" ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="CloudArrowUpIcon" size={13} />}
                  {csvStep === "importing" ? "Importing..." : "Import Valid Rows"}
                </button>
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto rounded-xl border border-red-50">
              <table className="w-full text-xs font-body">
                <thead className="bg-red-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-heading font-600 text-gray-600">Name</th>
                    <th className="px-3 py-2 text-left font-heading font-600 text-gray-600">Class</th>
                    <th className="px-3 py-2 text-left font-heading font-600 text-gray-600">Section</th>
                    <th className="px-3 py-2 text-left font-heading font-600 text-gray-600">Phone</th>
                    <th className="px-3 py-2 text-left font-heading font-600 text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {csvRows.map((row, i) => (
                    <tr key={i} className={`border-t border-red-50 ${row.valid ? "" : "bg-red-50/50"}`}>
                      <td className="px-3 py-2 text-gray-800">{row.student_name}</td>
                      <td className="px-3 py-2 text-gray-800">{row.class}</td>
                      <td className="px-3 py-2 text-gray-800">{row.section}</td>
                      <td className="px-3 py-2 text-gray-800">{row.parent_phone}</td>
                      <td className="px-3 py-2">
                        {row.valid
                          ? <span className="inline-flex items-center gap-1 text-green-600"><Icon name="CheckCircleIcon" size={12} />Valid</span>
                          : <span className="inline-flex items-center gap-1 text-red-500"><Icon name="ExclamationCircleIcon" size={12} />{row.error}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Filters + Table */}
        <div className="bg-white rounded-2xl border border-red-100 p-5">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search name or phone..."
              className="flex-1 min-w-[180px] px-3 py-2 border border-red-100 rounded-lg font-body text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
            <select value={filterClass} onChange={(e) => setFilterClass(e.target.value)}
              className="px-3 py-2 border border-red-100 rounded-lg font-body text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white">
              <option value="all">All Classes</option>
              {CLASS_LIST.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filterSection} onChange={(e) => setFilterSection(e.target.value)}
              className="px-3 py-2 border border-red-100 rounded-lg font-body text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white">
              <option value="all">All Sections</option>
              {SECTION_LIST.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <span className="font-body text-xs text-gray-500">{filtered.length} shown</span>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              <span className="ml-3 text-sm text-gray-500 font-body">Loading students...</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10">
              <Icon name="UserGroupIcon" size={32} className="text-gray-400 mx-auto mb-2 opacity-30" />
              <p className="font-body text-sm text-gray-500">{students.length === 0 ? "No students yet. Upload a CSV to get started." : "No students match the current filters."}</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-red-50 max-h-[420px] overflow-y-auto">
              <table className="w-full text-sm font-body">
                <thead className="bg-red-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-heading font-600 text-gray-600 text-xs">Name</th>
                    <th className="px-4 py-2.5 text-left font-heading font-600 text-gray-600 text-xs">Class</th>
                    <th className="px-4 py-2.5 text-left font-heading font-600 text-gray-600 text-xs">Section</th>
                    <th className="px-4 py-2.5 text-left font-heading font-600 text-gray-600 text-xs">Phone</th>
                    <th className="px-4 py-2.5 text-right font-heading font-600 text-gray-600 text-xs">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => (
                    <tr key={s.id} className="border-t border-red-50 hover:bg-red-50/30 transition-colors">
                      <td className="px-4 py-2.5 text-gray-800 font-body">{s.student_name}</td>
                      <td className="px-4 py-2.5 text-gray-800">{s.class}</td>
                      <td className="px-4 py-2.5 text-gray-800">{s.section}</td>
                      <td className="px-4 py-2.5 text-gray-800">{s.parent_phone}</td>
                      <td className="px-4 py-2.5 text-right">
                        <button onClick={() => deleteStudent(s.id)} className="w-7 h-7 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors ml-auto">
                          <Icon name="TrashIcon" size={12} className="text-white" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ─── PHONE MANAGER ────────────────────────────────────────────────────────────
interface BulkContact { id: string; phone_number: string; label: string; }

const PhoneManager: React.FC = () => {
  const [contacts, setContacts] = useState<BulkContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [newPhone, setNewPhone] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [csvRows, setCsvRows] = useState<{ phone: string; label: string; valid: boolean }[]>([]);
  const [csvStep, setCsvStep] = useState<"idle" | "preview" | "importing">("idle");
  const [toast, setToast] = useState({ visible: false, message: "" });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showToast = (message: string) => {
    setToast({ visible: true, message });
    setTimeout(() => setToast({ visible: false, message: "" }), 2500);
  };

  const loadContacts = useCallback(async () => {
    setLoading(true);
    const supabase = createClient();
    const { data, error } = await supabase.from("bulk_contacts").select("*").order("created_at", { ascending: false });
    if (error) console.error("Failed to load contacts:", error.message);
    else setContacts(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { loadContacts(); }, [loadContacts]);

  const addContact = async () => {
    const phone = newPhone.trim().replace(/\D/g, "").slice(-10);
    if (!/^[6-9]\d{9}$/.test(phone)) { alert("Enter a valid 10-digit Indian mobile number."); return; }
    setSaving(true);
    const supabase = createClient();
    const { data, error } = await supabase.from("bulk_contacts").upsert({ phone_number: phone, label: newLabel.trim() || null }, { onConflict: "phone_number" }).select().single();
    if (error) { alert("Failed to add: " + error.message); }
    else if (data) {
      setContacts((prev) => [data, ...prev.filter((c) => c.phone_number !== phone)]);
      setNewPhone(""); setNewLabel("");
      showToast("Contact added");
    }
    setSaving(false);
  };

  const deleteContact = async (id: string) => {
    const supabase = createClient();
    await supabase.from("bulk_contacts").delete().eq("id", id);
    setContacts((prev) => prev.filter((c) => c.id !== id));
    showToast("Contact removed");
  };

  const parseCsvPhones = (text: string) => {
    const lines = text.trim().split("\n");
    const startIdx = lines[0]?.toLowerCase().includes("phone") ? 1 : 0;
    return lines.slice(startIdx).map((line) => {
      const cols = line.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
      const rawPhone = cols[0] ?? "";
      const label = cols[1] ?? "";
      const phone = rawPhone.replace(/\D/g, "").slice(-10);
      const valid = /^[6-9]\d{9}$/.test(phone);
      return { phone, label, valid };
    }).filter((r) => r.phone.length > 0);
  };

  const handleCsvFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const rows = parseCsvPhones(ev.target?.result as string);
      setCsvRows(rows);
      setCsvStep("preview");
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const importCsvPhones = async () => {
    const valid = csvRows.filter((r) => r.valid);
    if (valid.length === 0) return;
    setCsvStep("importing");
    const supabase = createClient();
    const toInsert = valid.map((r) => ({ phone_number: r.phone, label: r.label || null }));
    const { error } = await supabase.from("bulk_contacts").upsert(toInsert, { onConflict: "phone_number", ignoreDuplicates: true });
    if (error) { alert("Import failed: " + error.message); }
    else { showToast(`${valid.length} contacts imported`); await loadContacts(); }
    setCsvStep("idle");
    setCsvRows([]);
  };

  return (
    <>
      <Toast message={toast.message} visible={toast.visible} />
      <div className="space-y-5">
        {/* Add single */}
        <div className="bg-white rounded-2xl border border-red-100 p-5">
          <h3 className="font-heading font-700 text-gray-800 text-sm mb-4 flex items-center gap-2">
            <Icon name="PlusCircleIcon" size={16} className="text-primary" />Add Phone Number
          </h3>
          <div className="flex flex-wrap gap-3">
            <input type="tel" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="10-digit mobile number"
              className="flex-1 min-w-[180px] px-4 py-2.5 border border-red-100 rounded-xl font-body text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
            <input type="text" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Label (optional)"
              className="flex-1 min-w-[140px] px-4 py-2.5 border border-red-100 rounded-xl font-body text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
            <button onClick={addContact} disabled={saving || !newPhone.trim()}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl font-heading font-600 text-sm hover:bg-primary-dark transition-colors disabled:opacity-60">
              <Icon name="PlusIcon" size={15} />{saving ? "Adding..." : "Add"}
            </button>
          </div>
        </div>

        {/* CSV Upload */}
        <div className="bg-white rounded-2xl border border-red-100 p-5">
          <h3 className="font-heading font-700 text-gray-800 text-sm mb-2 flex items-center gap-2">
            <Icon name="DocumentArrowUpIcon" size={16} className="text-primary" />Bulk Import via CSV
          </h3>
          <p className="font-body text-xs text-gray-500 mb-3">CSV format: <strong>Phone, Label</strong> (header row optional)</p>
          <button onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary/10 text-primary border border-primary/20 rounded-xl font-heading font-600 text-sm hover:bg-primary/20 transition-colors">
            <Icon name="DocumentArrowUpIcon" size={15} />Upload CSV
          </button>
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleCsvFile} />
        </div>

        {/* CSV Preview */}
        {csvStep !== "idle" && (
          <div className="bg-white rounded-2xl border border-red-100 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-700 text-gray-800 text-sm">
                Preview — {csvRows.filter((r) => r.valid).length} valid / {csvRows.length} rows
              </h3>
              <div className="flex gap-2">
                <button onClick={() => { setCsvStep("idle"); setCsvRows([]); }}
                  className="px-4 py-2 border border-red-100 text-gray-500 rounded-lg font-heading font-600 text-xs hover:bg-red-50 transition-colors">Cancel</button>
                <button onClick={importCsvPhones} disabled={csvStep === "importing"}
                  className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-lg font-heading font-600 text-xs hover:bg-primary-dark transition-colors disabled:opacity-60">
                  {csvStep === "importing" ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="CloudArrowUpIcon" size={13} />}
                  Import
                </button>
              </div>
            </div>
            <div className="max-h-48 overflow-y-auto rounded-xl border border-red-50">
              <table className="w-full text-xs font-body">
                <thead className="bg-red-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-heading font-600 text-gray-600">Phone</th>
                    <th className="px-3 py-2 text-left font-heading font-600 text-gray-600">Label</th>
                    <th className="px-3 py-2 text-left font-heading font-600 text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {csvRows.map((row, i) => (
                    <tr key={i} className={`border-t border-red-50 ${row.valid ? "" : "bg-red-50/50"}`}>
                      <td className="px-3 py-2 text-gray-800">{row.phone}</td>
                      <td className="px-3 py-2 text-gray-800">{row.label}</td>
                      <td className="px-3 py-2">
                        {row.valid
                          ? <span className="text-green-600 flex items-center gap-1"><Icon name="CheckCircleIcon" size={12} />Valid</span>
                          : <span className="text-red-500 flex items-center gap-1"><Icon name="ExclamationCircleIcon" size={12} />Invalid</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Contacts list */}
        <div className="bg-white rounded-2xl border border-red-100 p-5">
          <h3 className="font-heading font-700 text-gray-800 text-sm mb-4 flex items-center gap-2">
            <Icon name="PhoneIcon" size={16} className="text-primary" />Saved Numbers
            <span className="ml-1 text-xs text-gray-500 font-body">({contacts.length})</span>
          </h3>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              <span className="ml-3 text-sm text-gray-500 font-body">Loading...</span>
            </div>
          ) : contacts.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="PhoneIcon" size={28} className="text-gray-400 mx-auto mb-2 opacity-30" />
              <p className="font-body text-sm text-gray-500">No contacts yet</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {contacts.map((c) => (
                <div key={c.id} className="flex items-center justify-between px-4 py-3 bg-red-50/40 rounded-xl border border-red-50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name="PhoneIcon" size={14} className="text-primary" />
                    </div>
                    <div>
                      <p className="font-heading font-600 text-sm text-gray-800">{c.phone_number}</p>
                      {c.label && <p className="font-body text-xs text-gray-500">{c.label}</p>}
                    </div>
                  </div>
                  <button onClick={() => deleteContact(c.id)} className="w-7 h-7 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors">
                    <Icon name="TrashIcon" size={12} className="text-white" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ─── ANNOUNCEMENT MANAGER ─────────────────────────────────────────────────────
interface AnnouncementRecord {
  id: string; title: string; message: string; target_type: string;
  target_classes: string[] | null; target_sections: string[] | null;
  custom_numbers: string[] | null; delivery_method: string;
  number_count: number; delivery_status: string; sent_at: string;
}

const AnnouncementManager: React.FC = () => {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [targetType, setTargetType] = useState<"all" | "classes" | "custom">("all");
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [customNumbers, setCustomNumbers] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState<"sms" | "voice">("sms");
  const [previewNumbers, setPreviewNumbers] = useState<string[]>([]);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: "" });
  const [error, setError] = useState("");

  const showToast = (message: string) => {
    setToast({ visible: true, message });
    setTimeout(() => setToast({ visible: false, message: "" }), 2500);
  };

  const toggleClass = (cls: string) => setSelectedClasses((prev) => prev.includes(cls) ? prev.filter((c) => c !== cls) : [...prev, cls]);
  const toggleSection = (sec: string) => setSelectedSections((prev) => prev.includes(sec) ? prev.filter((s) => s !== sec) : [...prev, sec]);

  const fetchPreview = async () => {
    setPreviewing(true);
    setError("");
    try {
      const customArr = targetType === "custom" ? customNumbers.split(/[\n,]/).map((n) => n.trim()).filter(Boolean) : [];
      const res = await fetch("/api/msg91/fetch-numbers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ targetType, selectedClasses, selectedSections, customNumbers: customArr }),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); setPreviewNumbers([]); }
      else setPreviewNumbers(data.numbers ?? []);
    } catch { setError("Failed to fetch numbers"); }
    setPreviewing(false);
  };

  const sendAnnouncement = async () => {
    if (!title.trim()) { setError("Title is required"); return; }
    if (!message.trim()) { setError("Message is required"); return; }
    if (previewNumbers.length === 0) { setError("No recipients. Click Preview first."); return; }
    setSending(true);
    setError("");
    try {
      const endpoint = deliveryMethod === "sms" ? "/api/msg91/sms" : "/api/msg91/voice";
      const body = deliveryMethod === "sms"
        ? { title, message, recipients: previewNumbers }
        : { message: `${title}. ${message}`, recipients: previewNumbers };
      const res = await fetch(endpoint, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const result = await res.json();
      const status = result.success ? "success" : "failed";

      // Save to announcements table
      const supabase = createClient();
      const customArr = targetType === "custom" ? customNumbers.split(/[\n,]/).map((n) => n.trim()).filter(Boolean) : null;
      await supabase.from("announcements").insert({
        title, message, target_type: targetType,
        target_classes: selectedClasses.length > 0 ? selectedClasses : null,
        target_sections: selectedSections.length > 0 ? selectedSections : null,
        custom_numbers: customArr,
        delivery_method: deliveryMethod,
        number_count: previewNumbers.length,
        delivery_status: status,
        msg91_response: result,
      });

      if (result.error) { setError(result.error); }
      else {
        showToast(`Announcement sent to ${previewNumbers.length} recipients`);
        setTitle(""); setMessage(""); setPreviewNumbers([]); setCustomNumbers("");
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Send failed");
    }
    setSending(false);
  };

  return (
    <>
      <Toast message={toast.message} visible={toast.visible} />
      <div className="space-y-5 max-w-3xl">
        {/* Compose */}
        <div className="bg-white rounded-2xl border border-red-100 p-5">
          <h3 className="font-heading font-700 text-gray-800 text-sm mb-4 flex items-center gap-2">
            <Icon name="MegaphoneIcon" size={16} className="text-primary" />Compose Announcement
          </h3>
          <div className="space-y-3">
            <div>
              <label className="block font-heading font-600 text-xs text-gray-500 uppercase tracking-wide mb-1.5">Title</label>
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Announcement title"
                className="w-full px-4 py-2.5 border border-red-100 rounded-xl font-body text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
            </div>
            <div>
              <label className="block font-heading font-600 text-xs text-gray-500 uppercase tracking-wide mb-1.5">Message</label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder="Type your announcement message..."
                className="w-full px-4 py-2.5 border border-red-100 rounded-xl font-body text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none" />
            </div>
          </div>
        </div>

        {/* Target */}
        <div className="bg-white rounded-2xl border border-red-100 p-5">
          <h3 className="font-heading font-700 text-gray-800 text-sm mb-4 flex items-center gap-2">
            <Icon name="UsersIcon" size={16} className="text-primary" />Target Recipients
          </h3>
          <div className="flex gap-2 mb-4">
            {(["all", "classes", "custom"] as const).map((t) => (
              <button key={t} onClick={() => { setTargetType(t); setPreviewNumbers([]); }}
                className={`px-4 py-2 rounded-lg font-heading font-600 text-xs transition-colors capitalize ${targetType === t ? "bg-primary text-white" : "bg-red-50 text-gray-700 hover:bg-red-100"}`}>
                {t === "all" ? "All Students" : t === "classes" ? "By Class" : "Custom Numbers"}
              </button>
            ))}
          </div>
          {targetType === "classes" && (
            <div className="space-y-3">
              <div>
                <p className="font-heading font-600 text-xs text-gray-500 uppercase tracking-wide mb-2">Classes</p>
                <div className="flex flex-wrap gap-2">
                  {CLASS_LIST.map((cls) => (
                    <button key={cls} onClick={() => toggleClass(cls)}
                      className={`px-3 py-1.5 rounded-lg font-heading font-600 text-xs transition-colors ${selectedClasses.includes(cls) ? "bg-primary text-white" : "bg-red-50 text-gray-700 hover:bg-red-100"}`}>
                      {cls}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-heading font-600 text-xs text-gray-500 uppercase tracking-wide mb-2">Sections (optional)</p>
                <div className="flex flex-wrap gap-2">
                  {SECTION_LIST.map((sec) => (
                    <button key={sec} onClick={() => toggleSection(sec)}
                      className={`px-3 py-1.5 rounded-lg font-heading font-600 text-xs transition-colors ${selectedSections.includes(sec) ? "bg-primary text-white" : "bg-red-50 text-gray-700 hover:bg-red-100"}`}>
                      {sec}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {targetType === "custom" && (
            <div>
              <label className="block font-heading font-600 text-xs text-gray-500 uppercase tracking-wide mb-1.5">Phone Numbers (one per line or comma-separated)</label>
              <textarea value={customNumbers} onChange={(e) => setCustomNumbers(e.target.value)} rows={4} placeholder="9876543210&#10;9876543211&#10;..."
                className="w-full px-4 py-2.5 border border-red-100 rounded-xl font-body text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none" />
            </div>
          )}
        </div>

        {/* Delivery Method */}
        <div className="bg-white rounded-2xl border border-red-100 p-5">
          <h3 className="font-heading font-700 text-gray-800 text-sm mb-3 flex items-center gap-2">
            <Icon name="DevicePhoneMobileIcon" size={16} className="text-primary" />Delivery Method
          </h3>
          <div className="flex gap-3">
            <button onClick={() => setDeliveryMethod("sms")}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-heading font-600 text-sm transition-colors border ${deliveryMethod === "sms" ? "bg-primary text-white border-primary" : "bg-white text-gray-700 border-red-100 hover:border-primary/50"}`}>
              <Icon name="ChatBubbleLeftEllipsisIcon" size={15} />SMS
            </button>
            <button onClick={() => setDeliveryMethod("voice")}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-heading font-600 text-sm transition-colors border ${deliveryMethod === "voice" ? "bg-primary text-white border-primary" : "bg-white text-gray-700 border-red-100 hover:border-primary/50"}`}>
              <Icon name="PhoneArrowUpRightIcon" size={15} />Voice Call
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2 text-red-600 text-sm font-body">
            <Icon name="ExclamationCircleIcon" size={16} className="mt-0.5 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Preview + Send */}
        <div className="bg-white rounded-2xl border border-red-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading font-700 text-gray-800 text-sm">Recipients Preview</h3>
              {previewNumbers.length > 0 && <p className="font-body text-xs text-gray-500 mt-0.5">{previewNumbers.length} numbers ready</p>}
            </div>
            <button onClick={fetchPreview} disabled={previewing}
              className="flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary border border-primary/20 rounded-lg font-heading font-600 text-sm hover:bg-primary/20 transition-colors disabled:opacity-60">
              {previewing ? <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" /> : <Icon name="EyeIcon" size={14} />}
              {previewing ? "Loading..." : "Preview"}
            </button>
          </div>
          {previewNumbers.length > 0 && (
            <div className="bg-red-50/40 rounded-xl p-3 mb-4 max-h-32 overflow-y-auto">
              <div className="flex flex-wrap gap-1.5">
                {previewNumbers.slice(0, 50).map((n, i) => (
                  <span key={i} className="px-2 py-0.5 bg-white border border-red-100 rounded-md font-body text-xs text-gray-700">{n}</span>
                ))}
                {previewNumbers.length > 50 && <span className="px-2 py-0.5 bg-primary/10 text-primary rounded-md font-body text-xs">+{previewNumbers.length - 50} more</span>}
              </div>
            </div>
          )}
          <button onClick={sendAnnouncement} disabled={sending || previewNumbers.length === 0 || !title.trim() || !message.trim()}
            className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-heading font-700 text-sm hover:bg-primary-dark transition-colors disabled:opacity-60">
            {sending ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="PaperAirplaneIcon" size={15} />}
            {sending ? "Sending..." : `Send to ${previewNumbers.length} Recipients`}
          </button>
        </div>
      </div>
    </>
  );
};

// ─── HISTORY MANAGER ──────────────────────────────────────────────────────────
const HistoryManager: React.FC = () => {
  const [history, setHistory] = useState<AnnouncementRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const supabase = createClient();
      const { data, error } = await supabase.from("announcements").select("*").order("sent_at", { ascending: false }).limit(100);
      if (error) console.error("Failed to load history:", error.message);
      else setHistory(data ?? []);
      setLoading(false);
    };
    load();
  }, []);

  const statusColor = (status: string) => {
    if (status === "success") return "bg-green-100 text-green-700";
    if (status === "failed") return "bg-red-100 text-red-600";
    return "bg-yellow-100 text-yellow-700";
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-red-100 p-5">
        <h3 className="font-heading font-700 text-gray-800 text-sm mb-4 flex items-center gap-2">
          <Icon name="ClockIcon" size={16} className="text-primary" />Announcement History
          <span className="ml-1 text-xs text-gray-500 font-body">({history.length} records)</span>
        </h3>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            <span className="ml-3 text-sm text-gray-500 font-body">Loading history...</span>
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-10">
            <Icon name="ClockIcon" size={32} className="text-gray-400 mx-auto mb-2 opacity-30" />
            <p className="font-body text-sm text-gray-500">No announcements sent yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {history.map((item) => (
              <div key={item.id} className="flex items-start gap-4 p-4 bg-red-50/30 rounded-xl border border-red-50">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon name={item.delivery_method === "voice" ? "PhoneArrowUpRightIcon" : "ChatBubbleLeftEllipsisIcon"} size={16} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <p className="font-heading font-700 text-sm text-gray-800 truncate">{item.title}</p>
                    <span className={`px-2.5 py-0.5 rounded-full font-heading font-600 text-xs flex-shrink-0 ${statusColor(item.delivery_status)}`}>
                      {item.delivery_status}
                    </span>
                  </div>
                  {item.message && <p className="font-body text-xs text-gray-500 mt-0.5 line-clamp-2">{item.message}</p>}
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    <span className="font-body text-xs text-gray-500 flex items-center gap-1">
                      <Icon name="UsersIcon" size={11} />{item.number_count} recipients
                    </span>
                    <span className="font-body text-xs text-gray-500 capitalize flex items-center gap-1">
                      <Icon name="TagIcon" size={11} />{item.target_type}
                    </span>
                    <span className="font-body text-xs text-gray-400">
                      {new Date(item.sent_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── MAIN ADMIN PANEL ─────────────────────────────────────────────────────────
const TABS: { id: AdminTab; label: string; icon: string }[] = [
  { id: "hero", label: "Hero Slider", icon: "PhotoIcon" },
  { id: "gallery", label: "Gallery Manager", icon: "CameraIcon" },
  { id: "links", label: "Gallery Links", icon: "LinkIcon" },
  { id: "videos", label: "Video Manager", icon: "VideoCameraIcon" },
  { id: "students", label: "Student Upload", icon: "TableCellsIcon" },
  { id: "phones", label: "Phone Upload", icon: "PhoneIcon" },
  { id: "announce", label: "Announcements", icon: "MegaphoneIcon" },
  { id: "history", label: "History", icon: "ClockIcon" },
  { id: "about", label: "About Us Image", icon: "InformationCircleIcon" },
];

const AdminPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<AdminTab>("hero");

  const activeTabInfo = TABS.find((t) => t.id === activeTab);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className="admin-sidebar w-60 flex-shrink-0 bg-white border-r border-red-100 flex flex-col min-h-screen sticky top-0 h-screen overflow-y-auto">
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 py-5 border-b border-red-100">
          <AppLogo size={36} />
          <div>
            <h1 className="font-heading font-800 text-gray-800 text-sm leading-tight">Royal Admin</h1>
            <p className="font-body text-gray-500 text-xs">Media Management Panel</p>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 px-3 space-y-0.5">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all font-heading font-600 text-sm ${
                activeTab === tab.id
                  ? "bg-primary text-white" :"!text-gray-800 hover:bg-red-50 hover:!text-primary"
              }`}
            >
              <Icon name={tab.icon} size={16} className={activeTab === tab.id ? "text-white" : "text-primary"} />
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Sidebar footer */}
        <div className="px-5 py-4 border-t border-red-100">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="BoltIcon" size={13} className="text-primary" />
            <p className="font-body text-xs text-gray-500 font-600">Royal Schools</p>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <div className="bg-white border-b border-red-100 sticky top-0 z-40 flex items-center justify-between px-6 h-14">
          <div className="flex items-center gap-2">
            {activeTabInfo && (
              <>
                <Icon name={activeTabInfo.icon} size={18} className="text-primary" />
                <h2 className="font-heading font-700 text-gray-800 text-base">{activeTabInfo.label}</h2>
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Link href="/homepage" className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-primary transition-colors font-heading font-600 px-3 py-1.5 border border-red-100 rounded-lg">
              <Icon name="ArrowTopRightOnSquareIcon" size={13} />View Site
            </Link>
            <Link href="/homepage" className="flex items-center gap-1.5 text-xs text-white bg-primary hover:bg-primary-dark transition-colors font-heading font-600 px-3 py-1.5 rounded-lg">
              <Icon name="ArrowRightOnRectangleIcon" size={13} />Sign Out
            </Link>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6">
          {activeTab === "hero" && <HeroSliderManager />}
          {activeTab === "gallery" && <GalleryManager />}
          {activeTab === "links" && <GalleryLinksManager />}
          {activeTab === "videos" && <VideosManager />}
          {activeTab === "students" && <StudentManager />}
          {activeTab === "phones" && <PhoneManager />}
          {activeTab === "announce" && <AnnouncementManager />}
          {activeTab === "history" && <HistoryManager />}
          {activeTab === "about" && <AboutUsImageManager />}
        </div>
      </div>
    </div>
  );
};

// ─── PAGE EXPORT ──────────────────────────────────────────────────────────────
export default function AdminPanelPage() {
  const [loggedIn, setLoggedIn] = useState(false);
  if (!loggedIn) return <LoginScreen onLogin={() => setLoggedIn(true)} />;
  return <AdminPanel />;
}
